import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  VaultFolderResponseDto,
  VaultFolderCreateDto,
  VaultFolderUpdateDto,
  VaultCreateDto,
  VaultUpdateDto,
  VaultDeleteDto,
  VaultCheckPasswordDto,
  VaultChangeMasterPasswordDto,
} from '../../types/api';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../config/api';

export type VaultFolderItem = VaultFolderResponseDto;

export interface VaultFolderState {
  loading: boolean;
  items: VaultFolderItem[];
  error: string | null;
  currentOwnerId: string | null;
  selectedFolderId: string | null;
  expandedFolderIds: string[];
  isModalOpen: boolean;
  modalMode: 'add' | 'edit';
  operationLoading: {
    list: boolean;
    get: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  errors: {
    list: string | null;
    get: string | null;
    create: string | null;
    update: string | null;
    delete: string | null;
  };
}

const initialOperationLoading = {
  list: false,
  get: false,
  create: false,
  update: false,
  delete: false,
};

const initialErrors = {
  list: null as string | null,
  get: null as string | null,
  create: null as string | null,
  update: null as string | null,
  delete: null as string | null,
};

const initialState: VaultFolderState = {
  loading: false,
  items: [],
  error: null,
  currentOwnerId: null,
  selectedFolderId: null,
  expandedFolderIds: [],
  isModalOpen: false,
  modalMode: 'add',
  operationLoading: { ...initialOperationLoading },
  errors: { ...initialErrors },
};

// --- Async thunks ---

export const getVaultFolders = createAsyncThunk(
  'vaultFolder/getVaultFolders',
  async (_, { rejectWithValue }) => {
    try {
      return await apiGet<VaultFolderItem[]>('/api/VaultFolder');
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Klasörler yüklenirken hata oluştu');
    }
  }
);

export const getVaultFolder = createAsyncThunk(
  'vaultFolder/getVaultFolder',
  async (id: string, { rejectWithValue }) => {
    try {
      return await apiGet<VaultFolderItem>(`/api/VaultFolder/${id}`);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Klasör yüklenirken hata oluştu');
    }
  }
);

export const getVaultFoldersByOwner = createAsyncThunk(
  'vaultFolder/getVaultFoldersByOwner',
  async (ownerId: string, { rejectWithValue }) => {
    try {
      const items = await apiGet<VaultFolderItem[]>(`/api/VaultFolder/owner/${ownerId}`);
      return { ownerId, items };
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Klasörler yüklenirken hata oluştu');
    }
  }
);

export const createVaultFolder = createAsyncThunk(
  'vaultFolder/createVaultFolder',
  async (body: VaultFolderCreateDto, { rejectWithValue }) => {
    try {
      return await apiPost<VaultFolderItem>('/api/VaultFolder', body);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Klasör oluşturulurken hata oluştu');
    }
  }
);

export const updateVaultFolder = createAsyncThunk(
  'vaultFolder/updateVaultFolder',
  async (
    { id, body }: { id: string; body: VaultFolderUpdateDto },
    { rejectWithValue }
  ) => {
    try {
      const item = await apiPatch<VaultFolderItem>(`/api/VaultFolder/${id}`, body);
      return { id, item };
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Klasör güncellenirken hata oluştu');
    }
  }
);

export const deleteVaultFolder = createAsyncThunk(
  'vaultFolder/deleteVaultFolder',
  async (
    { id, ownerId }: { id: string; ownerId: string },
    { rejectWithValue }
  ) => {
    try {
      const path = `/api/VaultFolder/${id}?ownerId=${encodeURIComponent(ownerId)}`;
      await apiDelete(path);
      return id;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Klasör silinirken hata oluştu');
    }
  }
);

export const createVault = createAsyncThunk(
  'vaultFolder/createVault',
  async (body: VaultCreateDto, { rejectWithValue }) => {
    try {
      return await apiPost<{ id: string; ownerId: string; folderId?: string | null; name?: string | null; createdAt: string; updatedAt: string }>('/api/Vault', body);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Kasa oluşturulurken hata oluştu');
    }
  }
);

/** Update a vault (e.g. rename). Requires ownerId as query param. */
export const updateVault = createAsyncThunk(
  'vaultFolder/updateVault',
  async (
    { id, ownerId, body }: { id: string; ownerId: string; body: VaultUpdateDto },
    { rejectWithValue }
  ) => {
    try {
      const path = `/api/Vault/${id}?ownerId=${encodeURIComponent(ownerId)}`;
      return await apiPatch<{ id: string; ownerId: string; folderId?: string | null; name?: string | null; createdAt: string; updatedAt: string }>(path, body);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Kasa güncellenirken hata oluştu');
    }
  }
);

/** Delete a vault (table). Requires ownerId. Optional body (VaultDeleteDto) for vaultMasterPassword. Call getVaultFoldersByOwner after to refresh. */
export const deleteVault = createAsyncThunk(
  'vaultFolder/deleteVault',
  async (
    { id, ownerId, body }: { id: string; ownerId: string; body?: VaultDeleteDto },
    { rejectWithValue }
  ) => {
    try {
      const path = `/api/Vault/${id}?ownerId=${encodeURIComponent(ownerId)}`;
      await apiDelete(path, body ?? undefined);
      return id;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Kasa silinirken hata oluştu');
    }
  }
);

/** POST /api/Vault/checkIfUserKnowsPassword – returns true if the user knows the vault master password. */
export const checkIfUserKnowsPassword = createAsyncThunk(
  'vaultFolder/checkIfUserKnowsPassword',
  async (body: VaultCheckPasswordDto, { rejectWithValue }) => {
    try {
      return await apiPost<boolean>('/api/Vault/checkIfUserKnowsPassword', body);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Şifre kontrol edilirken hata oluştu');
    }
  }
);

/** POST /api/Vault/{id}/change-master-password – change vault master password. Returns 204 on success. */
export const changeVaultMasterPassword = createAsyncThunk(
  'vaultFolder/changeVaultMasterPassword',
  async (
    { id, body }: { id: string; body: VaultChangeMasterPasswordDto },
    { rejectWithValue }
  ) => {
    try {
      await apiPost<void>(`/api/Vault/${id}/change-master-password`, body);
      return id;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Kasa ana şifresi değiştirilirken hata oluştu');
    }
  }
);

// --- Slice ---

const vaultFolderSlice = createSlice({
  name: 'vaultFolder',
  initialState,
  reducers: {
    setCurrentOwnerId: (state, action: PayloadAction<string | null>) => {
      state.currentOwnerId = action.payload;
    },
    setSelectedFolderId: (state, action: PayloadAction<string | null>) => {
      state.selectedFolderId = action.payload;
    },
    setExpandedFolderIds: (state, action: PayloadAction<string[]>) => {
      state.expandedFolderIds = action.payload;
    },
    toggleFolderExpanded: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.expandedFolderIds.indexOf(id);
      if (idx === -1) state.expandedFolderIds.push(id);
      else state.expandedFolderIds.splice(idx, 1);
    },
    setVaultFolderModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },
    setVaultFolderModalMode: (state, action: PayloadAction<'add' | 'edit'>) => {
      state.modalMode = action.payload;
    },
    openVaultFolderEditModal: (state, action: PayloadAction<string>) => {
      state.selectedFolderId = action.payload;
      state.modalMode = 'edit';
      state.isModalOpen = true;
    },
    openVaultFolderAddModal: (state) => {
      state.selectedFolderId = null;
      state.modalMode = 'add';
      state.isModalOpen = true;
    },
    resetVaultFolderSelection: (state) => {
      state.currentOwnerId = null;
      state.selectedFolderId = null;
      state.expandedFolderIds = [];
      state.isModalOpen = false;
      state.modalMode = 'add';
    },
    clearVaultFolderError: (state) => {
      state.error = null;
      state.errors = { ...initialErrors };
    },
    clearVaultFolderItems: (state) => {
      state.items = [];
      state.currentOwnerId = null;
    },
  },
  extraReducers: (builder) => {
    // getVaultFolders
    builder.addCase(getVaultFolders.pending, (state) => {
      state.operationLoading.list = true;
      state.error = null;
      state.errors.list = null;
    });
    builder.addCase(getVaultFolders.fulfilled, (state, action) => {
      state.operationLoading.list = false;
      state.items = Array.isArray(action.payload) ? action.payload : [];
      state.errors.list = null;
    });
    builder.addCase(getVaultFolders.rejected, (state, action) => {
      state.operationLoading.list = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Klasörler yüklenirken hata oluştu';
      state.error = msg;
      state.errors.list = msg;
    });

    // getVaultFolder
    builder.addCase(getVaultFolder.pending, (state) => {
      state.operationLoading.get = true;
      state.errors.get = null;
    });
    builder.addCase(getVaultFolder.fulfilled, (state, action) => {
      state.operationLoading.get = false;
      const item = action.payload;
      const idx = state.items.findIndex((i) => i.id === item.id);
      if (idx >= 0) state.items[idx] = item;
      else state.items.push(item);
      state.errors.get = null;
    });
    builder.addCase(getVaultFolder.rejected, (state, action) => {
      state.operationLoading.get = false;
      state.errors.get = typeof action.payload === 'string' ? action.payload : 'Klasör yüklenirken hata oluştu';
    });

    // getVaultFoldersByOwner
    builder.addCase(getVaultFoldersByOwner.pending, (state) => {
      state.operationLoading.list = true;
      state.error = null;
      state.errors.list = null;
    });
    builder.addCase(getVaultFoldersByOwner.fulfilled, (state, action) => {
      state.operationLoading.list = false;
      state.items = Array.isArray(action.payload.items) ? action.payload.items : [];
      state.currentOwnerId = action.payload.ownerId;
      state.errors.list = null;
    });
    builder.addCase(getVaultFoldersByOwner.rejected, (state, action) => {
      state.operationLoading.list = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Klasörler yüklenirken hata oluştu';
      state.error = msg;
      state.errors.list = msg;
    });

    // createVaultFolder
    builder.addCase(createVaultFolder.pending, (state) => {
      state.operationLoading.create = true;
      state.errors.create = null;
    });
    builder.addCase(createVaultFolder.fulfilled, (state, action) => {
      state.operationLoading.create = false;
      const item = action.payload;
      if (item && item.id && !state.items.some((i) => i.id === item.id)) {
        state.items.push(item);
      }
      state.errors.create = null;
    });
    builder.addCase(createVaultFolder.rejected, (state, action) => {
      state.operationLoading.create = false;
      state.errors.create = typeof action.payload === 'string' ? action.payload : 'Klasör oluşturulurken hata oluştu';
    });

    // updateVaultFolder
    builder.addCase(updateVaultFolder.pending, (state) => {
      state.operationLoading.update = true;
      state.errors.update = null;
    });
    builder.addCase(updateVaultFolder.fulfilled, (state, action) => {
      state.operationLoading.update = false;
      const { id, item } = action.payload;
      const idx = state.items.findIndex((i) => i.id === id);
      if (idx >= 0 && item) state.items[idx] = item;
      state.errors.update = null;
    });
    builder.addCase(updateVaultFolder.rejected, (state, action) => {
      state.operationLoading.update = false;
      state.errors.update = typeof action.payload === 'string' ? action.payload : 'Klasör güncellenirken hata oluştu';
    });

    // deleteVaultFolder
    builder.addCase(deleteVaultFolder.pending, (state) => {
      state.operationLoading.delete = true;
      state.errors.delete = null;
    });
    builder.addCase(deleteVaultFolder.fulfilled, (state, action) => {
      state.operationLoading.delete = false;
      state.items = state.items.filter((i) => i.id !== action.payload);
      state.errors.delete = null;
    });
    builder.addCase(deleteVaultFolder.rejected, (state, action) => {
      state.operationLoading.delete = false;
      state.errors.delete = typeof action.payload === 'string' ? action.payload : 'Klasör silinirken hata oluştu';
    });
  },
});

export const {
  setCurrentOwnerId,
  setSelectedFolderId,
  setExpandedFolderIds,
  toggleFolderExpanded,
  setVaultFolderModalOpen,
  setVaultFolderModalMode,
  openVaultFolderEditModal,
  openVaultFolderAddModal,
  resetVaultFolderSelection,
  clearVaultFolderError,
  clearVaultFolderItems,
} = vaultFolderSlice.actions;
export default vaultFolderSlice.reducer;
